'use client';

import { confirmTransaction, DEFAULT_LANDING_CONFIG, type SigningDeps, signTransaction, useActiveWallet, useFinalizeGiftSend, useSendGift as useSendGiftV2, useTurnkeySigningDeps, useWallets } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import { useImpersonationStore } from '@/lib/impersonation';
import { getActiveNetwork } from '@/lib/network/active';
import { reportError, trackAction } from '@/lib/telemetry';
import { logger } from '@/lib/telemetry/logger';
import { getConnection } from '@/lib/tx-engine/connection';
import { showToast } from '@/lib/ui/toast';
import { useInvalidateWalletBalances } from '@/queries/wallet-balance';

/**
 * Send-gift hook — backend returns a partial tx pre-signed by the
 * backend authority; the frontend signs with the sender's Turnkey
 * wallet and submits. The tx contains:
 *   1. SPL transfer_checked (sender → GiftAdmin treasury ATA)
 *   2. create_gift Anchor ix
 *
 * After confirmation, the frontend POSTs to /v1/gifts/send/finalize
 * with the signature so `gifts.create_tx` is populated for the
 * activity-feed join.
 */

export type SendGiftStep = 'idle' | 'authorizing' | 'signing' | 'submitting' | 'confirming' | 'confirmed' | 'failed';
export type GiftPlatform = 'morfi' | 'x';
export type GiftAsset = {
  kind: 'usdc';
  amountBaseUnits: string;
} | {
  kind: 'outcome_token';
  mintPubkey: string;
  amountBaseUnits: string;
};
export interface SendGiftInput {
  platform: GiftPlatform;
  recipientHandle: string;
  asset: GiftAsset;
  message: string;
  anonymous: boolean;
  /**
   * Sender wallet id. Optional — when omitted, falls back to the
   * active wallet then the first wallet in the list. The modal passes
   * this explicitly so the user-picked wallet (not the active one) is
   * signed against.
   */
  senderWalletId?: string;
}
export interface SendGiftResult {
  giftPda: string;
  opaqueId: string;
  signature: string;
  permalinkUrl: string;
}
export interface UseSendGiftReturn {
  step: SendGiftStep;
  error: string | null;
  result: SendGiftResult | null;
  submit: (input: SendGiftInput) => Promise<SendGiftResult | null>;
  reset: () => void;
}
interface SendGiftServerResponse {
  giftPda: string;
  opaqueId: string;
  partiallySignedTxBase64: string;
  blockhash: string;
  lastValidBlockHeight: number;
  expiresAt: number;
}
function getExplorerUrl(signature: string): string {
  const network = getActiveNetwork();
  if (network === 'mainnet') {
    return `https://explorer.solana.com/tx/${signature}`;
  }
  return `https://explorer.solana.com/tx/${signature}?cluster=${network}`;
}
const IS_DEV: boolean = process.env.NODE_ENV === 'development';
export function useSendGift(): UseSendGiftReturn {
  const invalidateWalletBalances = useInvalidateWalletBalances();
  const sendMutation = useSendGiftV2();
  const finalizeMutation = useFinalizeGiftSend();
  const signingDeps: SigningDeps = useTurnkeySigningDeps();
  const {
    activeWallet
  } = useActiveWallet();
  const {
    wallets
  } = useWallets();
  const impersonationActive = useImpersonationStore(s => s.isActive);
  const [step, setStep] = useState<SendGiftStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendGiftResult | null>(null);
  const t = useTranslations('gift.send.toast');
  function reset(): void {
    setStep('idle');
    setError(null);
    setResult(null);
  }
  async function submit(input: SendGiftInput): Promise<SendGiftResult | null> {
    setStep('authorizing');
    setError(null);
    setResult(null);
    showToast({
      id: TOAST_IDS.GIFT_SENDING,
      message: t('preparing'),
      variant: 'loading',
      duration: Number.POSITIVE_INFINITY
    });
    trackAction('gift_send_started', {
      platform: input.platform,
      anonymous: String(input.anonymous)
    });
    const availableWallets = wallets.filter(wallet => wallet.isAvailable !== false);
    const sender = (input.senderWalletId !== undefined ? availableWallets.find(wallet_0 => wallet_0.id === input.senderWalletId) : undefined) ?? activeWallet ?? availableWallets[0];
    if (sender === undefined) {
      setStep('failed');
      setError('No active wallet');
      showToast({
        id: TOAST_IDS.GIFT_SENDING,
        message: t('failed'),
        variant: 'error'
      });
      return null;
    }
    try {
      // 1. Backend builds + pre-signs the partial tx (transfer +
      //    create_gift). Backend also writes the gifts row in pending
      //    state and resolves X handle → id_str if applicable.
      const vars = {
        recipientPlatform: input.platform,
        recipientHandle: input.recipientHandle,
        asset: input.asset,
        ...(input.message.length > 0 ? {
          message: input.message
        } : {}),
        ...(input.anonymous ? {
          anonymous: true
        } : {})
      } as const;
      const raw = await sendMutation.mutateAsync(vars as never);
      const auth = ((raw as {
        data?: SendGiftServerResponse;
      }).data ?? raw as SendGiftServerResponse) as SendGiftServerResponse;

      // 2. Pass the full partial tx (with the backend authority signature
      //    already in its slot, the sender slot empty) to Turnkey. Turnkey's
      //    signTransaction expects the FULL unsigned-or-partial tx bytes —
      //    NOT just the message — and returns the fully-signed tx bytes
      //    with the sender's signature overlaid in the right slot.
      setStep('signing');
      showToast({
        id: TOAST_IDS.GIFT_SENDING,
        message: t('signing'),
        variant: 'loading',
        duration: Number.POSITIVE_INFINITY
      });
      const partiallySignedBytes = Uint8Array.from(Buffer.from(auth.partiallySignedTxBase64, 'base64'));
      const fullySignedBytes = await signTransaction({
        address: sender.address,
        type: sender.type
      }, partiallySignedBytes, signingDeps, logger, impersonationActive, IS_DEV);

      // 3. Submit + confirm using the tx's blockhash (NOT a fresh one).
      setStep('submitting');
      const connection = getConnection();
      const signature = await connection.sendRawTransaction(fullySignedBytes, {
        skipPreflight: false,
        maxRetries: 3
      });
      setStep('confirming');
      const confirmation = await confirmTransaction(connection, signature, {
        blockhash: auth.blockhash,
        lastValidBlockHeight: auth.lastValidBlockHeight
      }, DEFAULT_LANDING_CONFIG, `gift-send:${signature.slice(0, 8)}`, logger);
      if (confirmation.status !== 'confirmed') {
        const reason = confirmation.status === 'failed' ? `Reverted: ${JSON.stringify(confirmation.error.details?.confirmationError ?? confirmation.error.code)}` : confirmation.status === 'expired' ? 'Tx expired — try again.' : confirmation.status === 'timeout' ? `Timed out (sig: ${signature})` : 'Cancelled';
        setStep('failed');
        setError(reason);
        showToast({
          id: TOAST_IDS.GIFT_SENDING,
          message: t('failed'),
          variant: 'error'
        });
        return null;
      }

      // 4. Tell the backend the signature so it populates create_tx.
      try {
        await finalizeMutation.mutateAsync({
          giftPda: auth.giftPda,
          signature
        } as never);
      } catch (finalizeErr) {
        // Non-fatal — the activity-feed join will be missing the
        // signature but the gift itself is real on chain.
        logger.warn('finalizeGiftSend failed; create_tx will stay null until reconcile', {
          giftPda: auth.giftPda,
          err: finalizeErr instanceof Error ? finalizeErr.message : String(finalizeErr)
        });
      }
      const permalinkUrl = `${window.location.origin}/gifts/${auth.opaqueId}`;
      const success: SendGiftResult = {
        giftPda: auth.giftPda,
        opaqueId: auth.opaqueId,
        signature,
        permalinkUrl
      };
      setResult(success);
      setStep('confirmed');
      invalidateWalletBalances();
      showToast({
        id: TOAST_IDS.GIFT_SENDING,
        message: t('success'),
        variant: 'success',
        duration: 5000,
        action: {
          label: t('viewOnExplorer'),
          onClick: (): void => {
            window.open(getExplorerUrl(signature), '_blank');
          }
        }
      });
      trackAction('gift_send_success', {
        platform: input.platform,
        signature
      });
      return success;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      reportError(err instanceof Error ? err : new Error(String(err)), {
        component: 'useSendGift'
      });
      setStep('failed');
      setError(message);
      showToast({
        id: TOAST_IDS.GIFT_SENDING,
        message: t('failed'),
        variant: 'error'
      });
      trackAction('gift_send_failed', {
        error: message.slice(0, 200)
      });
      return null;
    }
  }
  return {
    step,
    error,
    result,
    submit,
    reset
  };
}

/** Compat alias for callers still importing `useSendUsdcGift`. */
export const useSendUsdcGift: typeof useSendGift = useSendGift;